import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  ArrowUpward as UpIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  NavigateNext as NavigateNextIcon,
  Description as LogIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface FileItem {
  name: string;
  type: 'file' | 'directory' | 'link';
  size: number;
  permissions: string;
  mtime: number;
}

interface FileBrowserWindowProps {
  deviceId: string;
  deviceName?: string;
  websocket: WebSocket | null;
  terminal: any;
  inline?: boolean;
  open?: boolean;
  onClose?: () => void;
}

export const FileBrowserWindow: React.FC<FileBrowserWindowProps> = ({
  websocket,
  inline = false,
  open = true,
  onClose,
}) => {
  const [currentPath, setCurrentPath] = useState('/home/cnservice');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [warning, setWarning] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [connectionLost, setConnectionLost] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{fileName: string; percent: number} | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{
    fileName: string;
    percent: number;
    bytesSent?: number;
    totalBytes?: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hideProgressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingRequestRef = useRef<{
    type: string;
    resolve: (value: any) => void;
    reject: (error: Error) => void;
    startTime: number;
    timeoutId?: ReturnType<typeof setTimeout>;
    resetTimeout?: () => void;
  } | null>(null);
  // Store decoded binary chunks (Uint8Array) instead of base64 strings
  // This avoids invalid base64 when concatenating padded base64 chunks
  const downloadChunksRef = useRef<Uint8Array[]>([]);
  const downloadFileNameRef = useRef<string>('');

  // Send SFTP command and wait for response
  const sendSftpCommand = useCallback((type: string, params: any = {}): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!websocket || websocket.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const startTime = Date.now();
      pendingRequestRef.current = { type, resolve, reject, startTime };

      // Initial timeout (3 minutes) - will be reset on each chunk
      const createTimeout = () => {
        return setTimeout(() => {
          if (pendingRequestRef.current?.type === type && pendingRequestRef.current.startTime === startTime) {
            pendingRequestRef.current = null;
            reject(new Error('Operation timeout (3 minutes without progress)'));
          }
        }, 180000); // 3 minutes - reset on each chunk
      };

      const timeoutId = createTimeout();

      websocket.send(JSON.stringify({ type, ...params }));

      // Store timeout ID and reset function for cleanup/reset
      const request = pendingRequestRef.current;
      if (request) {
        request.timeoutId = timeoutId;
        request.resetTimeout = () => {
          if (request.timeoutId) clearTimeout(request.timeoutId);
          request.timeoutId = createTimeout();
        };
      }
    });
  }, [websocket]);

  // Listen to WebSocket messages for SFTP responses
  useEffect(() => {
    if (!websocket) return;

    const handleClose = () => {
      setConnectionLost(true);
      setError('Terminal connection closed. Please refresh the page or create a new SSH tunnel.');
    };

    const handleError = () => {
      setConnectionLost(true);
      setError('WebSocket connection error. The SSH tunnel may have been recreated. Please refresh the terminal.');
    };

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'sftp_list_result') {
            if (pendingRequestRef.current?.type === 'sftp_list') {
              // Clear timeout
              if (pendingRequestRef.current.timeoutId) {
                clearTimeout(pendingRequestRef.current.timeoutId);
              }
              pendingRequestRef.current.resolve(message);
              pendingRequestRef.current = null;
            }
          } else if (message.type === 'sftp_download_start') {
            // Start of chunked download
            downloadChunksRef.current = [];
            downloadFileNameRef.current = message.path.split('/').pop() || 'file';
            setDownloadProgress({
              fileName: downloadFileNameRef.current,
              percent: 0,
              bytesSent: 0,
              totalBytes: message.size
            });
          } else if (message.type === 'sftp_download_chunk') {
            // Decode base64 chunk to binary immediately
            // This avoids invalid base64 when concatenating padded chunks later
            try {
              const binaryString = atob(message.chunk);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              downloadChunksRef.current.push(bytes);
            } catch (decodeError) {
              // A dropped chunk would make the assembled file silently truncated. Fail
              // the download instead of resolving a corrupt blob. Only clear state for the
              // matching request — a stray chunk shouldn't clobber an unrelated download.
              console.error('Failed to decode chunk:', decodeError);
              if (pendingRequestRef.current?.type === 'sftp_download') {
                if (pendingRequestRef.current.timeoutId) {
                  clearTimeout(pendingRequestRef.current.timeoutId);
                }
                pendingRequestRef.current.reject(new Error('Download failed: a chunk could not be decoded (file would be corrupt)'));
                pendingRequestRef.current = null;
                downloadChunksRef.current = [];
                setDownloadProgress(null);
              }
              return;
            }
            setDownloadProgress({
              fileName: downloadFileNameRef.current,
              percent: message.progress,
              bytesSent: message.bytes_sent,
              totalBytes: message.total_bytes
            });

            // Reset timeout on each chunk (we're making progress!)
            if (pendingRequestRef.current?.type === 'sftp_download') {
              pendingRequestRef.current.resetTimeout?.();
            }
          } else if (message.type === 'sftp_download_complete') {
            // All chunks received - assemble binary data and trigger download
            if (pendingRequestRef.current?.type === 'sftp_download') {
              if (pendingRequestRef.current.timeoutId) {
                clearTimeout(pendingRequestRef.current.timeoutId);
              }

              // Concatenate all binary chunks into a single Uint8Array
              const totalLength = downloadChunksRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
              const fullContent = new Uint8Array(totalLength);
              let offset = 0;
              for (const chunk of downloadChunksRef.current) {
                fullContent.set(chunk, offset);
                offset += chunk.length;
              }

              pendingRequestRef.current.resolve({ binaryContent: fullContent, path: message.path });
              pendingRequestRef.current = null;

              // Clear chunks
              downloadChunksRef.current = [];
            }
          } else if (message.type === 'sftp_upload_start_ack') {
            // Upload start acknowledged
            if (pendingRequestRef.current?.type === 'sftp_upload_start') {
              if (pendingRequestRef.current.timeoutId) {
                clearTimeout(pendingRequestRef.current.timeoutId);
              }
              pendingRequestRef.current.resolve(message);
              pendingRequestRef.current = null;
            }
          } else if (message.type === 'sftp_upload_chunk_ack') {
            // Chunk acknowledged - update progress
            if (pendingRequestRef.current?.type === 'sftp_upload_chunk') {
              // Update progress
              setUploadProgress(prev => {
                // prev (not the stale closure value) carries the real filename set by
                // handleUpload; never let the bar jump backward.
                const fileName = prev?.fileName ?? '';
                const percent = Math.max(prev?.percent ?? 0, message.progress ?? 0);
                return prev && prev.percent === percent && prev.fileName === fileName
                  ? prev
                  : { fileName, percent };
              });
              // Reset timeout on each chunk ack (we're making progress!)
              if (pendingRequestRef.current) {
                pendingRequestRef.current.resetTimeout?.();
              }
              // Resolve chunk promise
              if (pendingRequestRef.current.timeoutId) {
                clearTimeout(pendingRequestRef.current.timeoutId);
              }
              pendingRequestRef.current.resolve(message);
              pendingRequestRef.current = null;
            }
          } else if (message.type === 'sftp_upload_result') {
            // Upload completed
            if (pendingRequestRef.current?.type === 'sftp_upload_complete') {
              if (pendingRequestRef.current.timeoutId) {
                clearTimeout(pendingRequestRef.current.timeoutId);
              }
              pendingRequestRef.current.resolve(message);
              pendingRequestRef.current = null;
            }
          } else if (message.type === 'sftp_upload_write_progress') {
            // Backend is writing assembled chunks to device via SFTP — reset timeout so
            // slow cellular writes don't trigger a 3-minute abort.
            const writePercent = message.progress ?? 0;
            setUploadProgress(prev => {
              // Keep the filename and never let the bar jump backward — the chunk-send
              // phase already advanced it toward 100%.
              const fileName = prev?.fileName ?? '';
              const percent = Math.max(prev?.percent ?? 0, writePercent);
              return prev && prev.percent === percent && prev.fileName === fileName
                ? prev
                : { fileName, percent };
            });
            pendingRequestRef.current?.resetTimeout?.();
          } else if (message.type === 'keepalive') {
            // Keepalive message - prevents timeout, no action needed
            console.debug('Received keepalive during file operation');
          } else if (message.type === 'error' && pendingRequestRef.current) {
            // Clear timeout
            if (pendingRequestRef.current.timeoutId) {
              clearTimeout(pendingRequestRef.current.timeoutId);
            }
            // Clear download state
            downloadChunksRef.current = [];
            setDownloadProgress(null);

            // Handle error responses for SFTP operations
            // Don't close WebSocket - just show error
            pendingRequestRef.current.reject(new Error(message.message || 'Operation failed'));
            pendingRequestRef.current = null;
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }
    };

    websocket.addEventListener('message', handleMessage);
    websocket.addEventListener('close', handleClose);
    websocket.addEventListener('error', handleError);

    return () => {
      websocket.removeEventListener('message', handleMessage);
      websocket.removeEventListener('close', handleClose);
      websocket.removeEventListener('error', handleError);
    };
  }, [websocket]);

  const browseDirectory = useCallback(async (path: string) => {
    // Don't attempt SFTP operations if connection is lost
    if (connectionLost) {
      return;
    }

    setLoading(true);
    setError('');
    setWarning('');

    try {
      const response = await sendSftpCommand('sftp_list', { path });

      const fileItems: FileItem[] = response.files
        .filter((f: any) => f.name !== '.' && f.name !== '..')
        .map((f: any) => ({
          name: f.name,
          type: f.is_dir ? 'directory' : 'file',
          size: f.size,
          permissions: f.permissions,
          mtime: f.mtime,
        }));

      fileItems.sort((a, b) => {
        if (a.type === 'directory' && b.type !== 'directory') return -1;
        if (a.type !== 'directory' && b.type === 'directory') return 1;
        return a.name.localeCompare(b.name);
      });

      setFiles(fileItems);
      setCurrentPath(path);
      setError(''); // Clear any previous errors on success
    } catch (err: any) {
      // Don't crash the terminal - show error but keep file browser functional
      const errorMsg = err.message || 'Unknown error';

      // Check if connection is lost (SFTP not available)
      if (errorMsg.includes('SFTP not available') || errorMsg.includes('not connected')) {
        setConnectionLost(true);
        setError('SSH tunnel has been recreated or closed. Please close this terminal and create a new SSH tunnel.');
        setFiles([]);
      }
      // Check if it's a timeout (slow connection)
      else if (errorMsg.includes('timeout') || errorMsg.includes('slow connection')) {
        setWarning(`Slow connection detected: ${errorMsg}. File browser may be slower than usual.`);
        // Keep the existing file list visible
      } else {
        setError(`Failed to browse ${path}: ${errorMsg}`);
        // Clear files only on hard errors (not timeouts)
        setFiles([]);
      }

      console.error('SFTP browse error:', err);
    } finally {
      setLoading(false);
    }
  }, [sendSftpCommand, connectionLost]);

  const downloadFile = useCallback(async (file: FileItem) => {
    if (file.type === 'directory') return;

    const fullPath = `${currentPath}/${file.name}`.replace('//', '/');

    // Clean up any existing timers
    if (hideProgressTimeoutRef.current) {
      clearTimeout(hideProgressTimeoutRef.current);
      hideProgressTimeoutRef.current = null;
    }

    try {
      // Send download request - backend will send progress updates
      // Chunks are decoded to binary in the message handler
      const response = await sendSftpCommand('sftp_download', { path: fullPath });

      // Response contains pre-decoded binary content (Uint8Array)
      const blob = new Blob([response.binaryContent]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Show 100% briefly before hiding
      setDownloadProgress({ fileName: file.name, percent: 100 });
      hideProgressTimeoutRef.current = setTimeout(() => {
        setDownloadProgress(null);
        hideProgressTimeoutRef.current = null;
      }, 1500);

    } catch (err: any) {
      if (hideProgressTimeoutRef.current) {
        clearTimeout(hideProgressTimeoutRef.current);
        hideProgressTimeoutRef.current = null;
      }
      setDownloadProgress(null);

      // Don't crash the terminal - just show error in file browser
      setError(`Download failed: ${err.message}`);
      console.error('Download error:', err);
    }
  }, [currentPath, sendSftpCommand]);

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clean up any existing timers
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    if (hideProgressTimeoutRef.current) {
      clearTimeout(hideProgressTimeoutRef.current);
      hideProgressTimeoutRef.current = null;
    }

    setUploadProgress({ fileName: file.name, percent: 0 });
    const targetPath = `${currentPath}/${file.name}`.replace('//', '/');

    try {
      // Read file as array buffer for chunked upload
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // Chunk size: 256KB (same as download)
      const CHUNK_SIZE = 256 * 1024;
      const totalSize = bytes.length;
      const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

      console.log(`Uploading ${file.name}: ${totalSize} bytes in ${totalChunks} chunks`);

      // Start chunked upload
      await sendSftpCommand('sftp_upload_start', { path: targetPath, total_size: totalSize });

      // Send chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunk = bytes.slice(start, end);

        // Convert chunk to base64 (avoid stack overflow with large chunks)
        // Process in smaller batches to avoid spreading too many args
        let binaryString = '';
        const batchSize = 8192; // Process 8KB at a time
        for (let j = 0; j < chunk.length; j += batchSize) {
          const batch = chunk.slice(j, Math.min(j + batchSize, chunk.length));
          binaryString += String.fromCharCode(...batch);
        }
        const base64Chunk = btoa(binaryString);

        // Send chunk and wait for acknowledgment
        await sendSftpCommand('sftp_upload_chunk', { chunk: base64Chunk });

        // Progress is updated by sftp_upload_chunk_ack handler
        console.log(`Sent chunk ${i + 1}/${totalChunks}`);
      }

      // Complete upload
      await sendSftpCommand('sftp_upload_complete', {});

      // Show 100% briefly
      setUploadProgress({ fileName: file.name, percent: 100 });

      // Hide progress and refresh directory
      hideProgressTimeoutRef.current = setTimeout(() => {
        setUploadProgress(null);
        browseDirectory(currentPath);
        hideProgressTimeoutRef.current = null;
      }, 1000);

    } catch (err: any) {
      if (hideProgressTimeoutRef.current) {
        clearTimeout(hideProgressTimeoutRef.current);
        hideProgressTimeoutRef.current = null;
      }
      setUploadProgress(null);
      setError(`Upload failed: ${err.message}`);
      console.error('Upload error:', err);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [currentPath, sendSftpCommand, browseDirectory]);

  const navigateToPath = useCallback((path: string) => {
    browseDirectory(path);
  }, [browseDirectory]);

  const goUp = useCallback(() => {
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
    navigateToPath(parentPath);
  }, [currentPath, navigateToPath]);

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Helper function to format timestamp (currently unused but kept for future use)
  // const formatTime = (timestamp: number): string => {
  //   if (!timestamp) return '';
  //   const date = new Date(timestamp * 1000);
  //   return date.toLocaleString();
  // };

  const pathParts = currentPath.split('/').filter(Boolean);

  // Initial directory browse with retry logic
  useEffect(() => {
    if (websocket?.readyState === WebSocket.OPEN) {
      // Delay to allow SFTP channel to initialize after WebSocket/SSH connection
      // 1 second should be enough for SFTP channel setup
      const timer = setTimeout(() => {
        browseDirectory(currentPath);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [websocket]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }
      if (hideProgressTimeoutRef.current) {
        clearTimeout(hideProgressTimeoutRef.current);
      }
    };
  }, []);

  if (!websocket) {
    return null;
  }

  // Shared content component
  const content = (
    <>
      {/* Toolbar */}
      <Paper sx={{ p: 0.5, borderRadius: 0, borderBottom: 1, borderColor: 'divider' }} elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.25 }}>
          <Tooltip title="Home"><IconButton size="small" sx={{ p: 0.25 }} onClick={() => navigateToPath('/')}><HomeIcon sx={{ fontSize: '1rem' }} /></IconButton></Tooltip>
          <Tooltip title="Up"><IconButton size="small" sx={{ p: 0.25 }} onClick={goUp} disabled={currentPath === '/'}><UpIcon sx={{ fontSize: '1rem' }} /></IconButton></Tooltip>
          <Tooltip title="Refresh"><IconButton size="small" sx={{ p: 0.25 }} onClick={() => browseDirectory(currentPath)}><RefreshIcon sx={{ fontSize: '1rem' }} /></IconButton></Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
          <Tooltip title="Upload"><IconButton size="small" sx={{ p: 0.25 }} color="primary" onClick={() => fileInputRef.current?.click()}><UploadIcon sx={{ fontSize: '1rem' }} /></IconButton></Tooltip>
        </Box>
        <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: '0.75rem' }} />} sx={{ fontSize: '0.7rem', minHeight: 'auto' }}>
          <Link component="button" underline="hover" onClick={() => navigateToPath('/')} sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem', lineHeight: 1 }}>
            <HomeIcon sx={{ mr: 0.25, fontSize: '0.75rem' }} />root
          </Link>
          {pathParts.map((part, index) => {
            const path = '/' + pathParts.slice(0, index + 1).join('/');
            return index === pathParts.length - 1 ? (
              <Typography
                key={path}
                sx={{
                  color: 'text.primary',
                  fontSize: '0.7rem',
                  lineHeight: 1
                }}>{part}</Typography>
            ) : (
              <Link key={path} component="button" underline="hover" onClick={() => navigateToPath(path)} sx={{ fontSize: '0.7rem', lineHeight: 1 }}>{part}</Link>
            );
          })}
        </Breadcrumbs>
      </Paper>

      {/* Quick access */}
      <Box sx={{ px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
        <Chip label="/home/cnservice" size="small" onClick={() => navigateToPath('/home/cnservice')} icon={<HomeIcon />} variant={currentPath === '/home/cnservice' ? 'filled' : 'outlined'} sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.75rem' } }} />
        <Chip label="/var/log" size="small" onClick={() => navigateToPath('/var/log')} icon={<LogIcon />} variant={currentPath === '/var/log' ? 'filled' : 'outlined'} sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.75rem' } }} />
        <Chip label="/tmp" size="small" onClick={() => navigateToPath('/tmp')} icon={<FolderIcon />} variant={currentPath === '/tmp' ? 'filled' : 'outlined'} sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.75rem' } }} />
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>{error}</Alert>}
      {warning && <Alert severity="warning" onClose={() => setWarning('')} sx={{ m: 1 }}>{warning}</Alert>}

      {/* Upload Progress */}
      {uploadProgress && (
        <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <UploadIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography variant="caption" sx={{ flex: 1, fontSize: '0.7rem' }}>
              Uploading: {uploadProgress.fileName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              {uploadProgress.percent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={uploadProgress.percent}
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      {/* Download Progress */}
      {downloadProgress && (
        <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <DownloadIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
            <Typography variant="caption" sx={{ flex: 1, fontSize: '0.7rem' }}>
              Downloading: {downloadProgress.fileName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              {downloadProgress.percent}%
            </Typography>
          </Box>
          {downloadProgress.totalBytes && (
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5, display: 'block' }}>
              {downloadProgress.bytesSent ? formatSize(downloadProgress.bytesSent) : '0 B'} / {formatSize(downloadProgress.totalBytes)}
            </Typography>
          )}
          <LinearProgress
            variant="determinate"
            value={downloadProgress.percent}
            color="success"
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      {/* File list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
        ) : (
          <List dense disablePadding>
            {files.map((file) => (
              <ListItemButton
                key={file.name}
                selected={selectedFile?.name === file.name}
                onClick={() => setSelectedFile(file)}
                onDoubleClick={() => {
                  if (file.type === 'directory') {
                    navigateToPath(`${currentPath}/${file.name}`.replace('//', '/'));
                  } else {
                    downloadFile(file);
                  }
                }}
                sx={{
                  py: 0.25,
                  px: 0.5,
                  minHeight: 'auto',
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 24 }}>
                  {file.type === 'directory' ? <FolderIcon color="primary" sx={{ fontSize: '1rem' }} /> : <FileIcon color="action" sx={{ fontSize: '1rem' }} />}
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${formatSize(file.size)}`}
                  sx={{ my: 0 }}
                  slotProps={{
                    primary: { sx: { fontSize: '0.75rem', lineHeight: 1.2 } },
                    secondary: { sx: { fontSize: '0.65rem', lineHeight: 1.2 } }
                  }} />
                {file.type === 'file' && (
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                      sx={{ p: 0.25 }}
                    >
                      <DownloadIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemButton>
            ))}
            {files.length === 0 && !loading && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}>No files</Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </>
  );

  // Inline mode
  if (inline) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {content}
      </Box>
    );
  }

  // Dialog mode
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
        <Typography variant="h6" sx={{
          fontSize: '1rem'
        }}>File Manager</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close file manager"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '70vh', display: 'flex', flexDirection: 'column' }}>
        {content}
      </DialogContent>
    </Dialog>
  );
};
